import { useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || process.env.VITE_API_URL || "http://localhost:8080";
const MCP_HEADER = { "X-MCP-Protocol": "1.0" };

const initialForm = {
  propertyId: "",
  landlordId: "",
  tenantId: "",
  guarantees: "fiador",
  rentAmount: 2500,
  dueDay: 5,
  startDate: "",
  endDate: "",
  adjustmentIndex: "IPCA",
  charges: "condominio,iptu",
  penalty: "2% + juros",
  type: "RESIDENCIAL",
  purpose: "Moradia",
  commercialPoint: false,
};

export default function Contracts({ keycloak }) {
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("list");
  const [documents, setDocuments] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [guarantees, setGuarantees] = useState([]);

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${keycloak.token}`,
    ...MCP_HEADER,
  };

  const loadContracts = async () => {
    const response = await fetch(`${API_URL}/api/contracts`, { headers });
    const data = await response.json();
    setContracts(data || []);
  };

  useEffect(() => {
    loadContracts().catch((error) => console.error("Erro ao carregar contratos", error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContractDetails = async (id) => {
    const [contractRes, docsRes, insRes, guaRes] = await Promise.all([
      fetch(`${API_URL}/api/contracts/${id}`, { headers }),
      fetch(`${API_URL}/api/contracts/${id}/documents`, { headers }),
      fetch(`${API_URL}/api/contracts/${id}/insurances`, { headers }),
      fetch(`${API_URL}/api/contracts/${id}/guarantees`, { headers }),
    ]);
    const contract = await contractRes.json();
    const docs = await docsRes.json();
    const ins = await insRes.json();
    const gua = await guaRes.json();
    setSelectedContract(contract);
    setDocuments(docs || []);
    setInsurances(ins || []);
    setGuarantees(gua || []);
    setActiveTab("details");
  };

  const createContract = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        guarantees: form.guarantees.split(",").map((item) => item.trim()),
        charges: form.charges.split(",").map((item) => item.trim()),
      };
      const response = await fetch(`${API_URL}/api/contracts`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      const created = await response.json();
      setContracts((prev) => [created, ...prev]);
      setForm(initialForm);
    } catch (error) {
      console.error("Erro ao criar contrato", error);
    } finally {
      setLoading(false);
    }
  };

  const renewContract = async (id) => {
    const newEndDate = prompt("Nova data de término (YYYY-MM-DD):");
    const newRent = prompt("Novo valor do aluguel:");
    if (newEndDate && newRent) {
      await fetch(`${API_URL}/api/contracts/${id}/renew`, {
        method: "POST",
        headers,
        body: JSON.stringify({ endDate: newEndDate, rentAmount: Number(newRent) }),
      });
      loadContracts();
    }
  };

  const rescindContract = async (id) => {
    const reason = prompt("Motivo da rescisão:");
    const endDate = prompt("Data de término (YYYY-MM-DD):");
    if (reason && endDate) {
      await fetch(`${API_URL}/api/contracts/${id}/rescind`, {
        method: "POST",
        headers,
        body: JSON.stringify({ reason, endDate }),
      });
      loadContracts();
    }
  };

  const adjustRent = async (id) => {
    const newRent = prompt("Novo valor do aluguel:");
    if (newRent) {
      await fetch(`${API_URL}/api/contracts/${id}/adjust`, {
        method: "POST",
        headers,
        body: JSON.stringify({ rentAmount: Number(newRent) }),
      });
      loadContracts();
    }
  };

  const generateCharges = async (id) => {
    await fetch(`${API_URL}/api/contracts/${id}/charges/generate`, {
      method: "POST",
      headers,
    });
    loadContractDetails(id);
  };

  const createDocument = async (contractId) => {
    const type = prompt("Tipo (ALVARA_FUNCIONAMENTO, INSPECAO, HABITE_SE, OUTROS):");
    const name = prompt("Nome do documento:");
    const number = prompt("Número:");
    const expirationDate = prompt("Data de validade (YYYY-MM-DD):");
    if (type && name && expirationDate) {
      await fetch(`${API_URL}/api/contracts/${contractId}/documents`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          type,
          name,
          number,
          expirationDate,
          issuingAuthority: prompt("Órgão emissor:") || "",
        }),
      });
      loadContractDetails(contractId);
    }
  };

  const createInsurance = async (contractId) => {
    const type = prompt("Tipo (LOCACAO, INCENDIO, RC, OUTROS):");
    const company = prompt("Seguradora:");
    const policyNumber = prompt("Número da apólice:");
    const premium = prompt("Prêmio:");
    const coverage = prompt("Valor da cobertura:");
    const endDate = prompt("Data de término (YYYY-MM-DD):");
    if (type && company && endDate) {
      await fetch(`${API_URL}/api/contracts/${contractId}/insurances`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          type,
          insuranceCompany: company,
          policyNumber,
          premium: Number(premium || 0),
          coverageAmount: Number(coverage || 0),
          endDate,
          startDate: new Date().toISOString().split("T")[0],
        }),
      });
      loadContractDetails(contractId);
    }
  };

  const createGuarantee = async (contractId) => {
    const type = prompt("Tipo (FIADOR, CAUCAO, SEGURO_FIANCA):");
    const name = prompt("Nome do fiador/garantidor:");
    const document = prompt("CPF/CNPJ:");
    const phone = prompt("Telefone:");
    const amount = prompt("Valor da garantia:");
    if (type && name) {
      await fetch(`${API_URL}/api/contracts/${contractId}/guarantees`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          type,
          guarantorName: name,
          guarantorDocument: document || "",
          guarantorPhone: phone || "",
          amount: Number(amount || 0),
          depositDate: new Date().toISOString().split("T")[0],
        }),
      });
      loadContractDetails(contractId);
    }
  };

  const createInspection = async (contractId, type) => {
    const date = prompt("Data da vistoria (YYYY-MM-DD):");
    const checklist = prompt("Checklist:");
    if (date) {
      await fetch(`${API_URL}/api/contracts/${contractId}/inspections`, {
        method: "POST",
        headers,
        body: JSON.stringify({ type, date, checklist }),
      });
      loadContractDetails(contractId);
    }
  };

  const createMaintenance = async (contractId) => {
    const description = prompt("Descrição do serviço:");
    const responsible = prompt("Responsável (LOCADOR/LOCATARIO):");
    const cost = prompt("Custo:");
    if (description && responsible) {
      await fetch(`${API_URL}/api/contracts/${contractId}/maintenance`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          description,
          responsible,
          requestedDate: new Date().toISOString().split("T")[0],
          cost: Number(cost || 0),
        }),
      });
      loadContractDetails(contractId);
    }
  };

  return (
    <div className="container">
      <section className="hero">
        <h1>Gestão de Contratos de Locação</h1>
        <p>
          Cadastre contratos, acompanhe o ciclo de vida, gere cobranças,
          reajustes e alertas de vencimento e garantias.
        </p>
      </section>

      <div style={{ display: "flex", gap: "1rem", marginBottom: "2rem", flexWrap: "wrap" }}>
        <button
          className={activeTab === "list" ? "btn-primary" : "btn-secondary"}
          onClick={() => setActiveTab("list")}
        >
          Lista de Contratos
        </button>
        <button
          className={activeTab === "new" ? "btn-primary" : "btn-secondary"}
          onClick={() => setActiveTab("new")}
        >
          Novo Contrato
        </button>
        {selectedContract && (
          <button
            className={activeTab === "details" ? "btn-primary" : "btn-secondary"}
            onClick={() => setActiveTab("details")}
          >
            Detalhes
          </button>
        )}
      </div>

      {activeTab === "new" && (
        <section className="panel">
          <h2 className="section-title">Novo Contrato</h2>
          <div className="grid grid-3">
            <input
              placeholder="Imóvel"
              value={form.propertyId}
              onChange={(e) => setForm({ ...form, propertyId: e.target.value })}
            />
            <input
              placeholder="Locador ID"
              value={form.landlordId}
              onChange={(e) => setForm({ ...form, landlordId: e.target.value })}
            />
            <input
              placeholder="Locatário ID"
              value={form.tenantId}
              onChange={(e) => setForm({ ...form, tenantId: e.target.value })}
            />
            <input
              type="number"
              placeholder="Valor do aluguel"
              value={form.rentAmount}
              onChange={(e) => setForm({ ...form, rentAmount: Number(e.target.value) })}
            />
            <input
              type="number"
              placeholder="Dia de vencimento"
              value={form.dueDay}
              onChange={(e) => setForm({ ...form, dueDay: Number(e.target.value) })}
            />
            <input
              placeholder="Índice de reajuste"
              value={form.adjustmentIndex}
              onChange={(e) => setForm({ ...form, adjustmentIndex: e.target.value })}
            />
            <input
              placeholder="Garantias (separadas por vírgula)"
              value={form.guarantees}
              onChange={(e) => setForm({ ...form, guarantees: e.target.value })}
            />
            <input
              placeholder="Encargos (condomínio, IPTU)"
              value={form.charges}
              onChange={(e) => setForm({ ...form, charges: e.target.value })}
            />
            <input
              placeholder="Multa"
              value={form.penalty}
              onChange={(e) => setForm({ ...form, penalty: e.target.value })}
            />
            <input
              type="date"
              placeholder="Início"
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
            />
            <input
              type="date"
              placeholder="Fim"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            />
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              <option value="RESIDENCIAL">Residencial</option>
              <option value="COMERCIAL">Comercial</option>
              <option value="TEMPORADA">Temporada</option>
            </select>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input
                type="checkbox"
                id="commercialPoint"
                checked={form.commercialPoint}
                onChange={(e) => setForm({ ...form, commercialPoint: e.target.checked })}
              />
              <label htmlFor="commercialPoint">Ponto Comercial</label>
            </div>
          </div>
          <button className="btn-primary" type="button" onClick={createContract} disabled={loading}>
            {loading ? "Salvando..." : "Cadastrar Contrato"}
          </button>
        </section>
      )}

      {activeTab === "list" && (
        <section>
          <h2 className="section-title">Contratos Cadastrados</h2>
          <div className="grid grid-3">
            {contracts.map((contract) => (
              <div key={contract.id} className="listing-card">
                <div className="tag">{contract.status}</div>
                {contract.commercialPoint && (
                  <div className="tag" style={{ marginTop: "0.5rem", background: "var(--brand-blue)20", color: "var(--brand-blue)" }}>
                    Ponto Comercial
                  </div>
                )}
                <h4>{contract.propertyId || "Imóvel sem nome"}</h4>
                <div>Locador: {contract.landlordId}</div>
                <div>Locatário: {contract.tenantId}</div>
                <div>Aluguel: R$ {contract.rentAmount?.toLocaleString("pt-BR")}</div>
                <div>Reajuste: {contract.adjustmentIndex}</div>
                <div>Período: {contract.startDate} até {contract.endDate}</div>
                <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  <button className="btn-secondary" onClick={() => loadContractDetails(contract.id)}>
                    Detalhes
                  </button>
                  <button className="btn-secondary" onClick={() => renewContract(contract.id)}>
                    Renovar
                  </button>
                  <button className="btn-secondary" onClick={() => rescindContract(contract.id)}>
                    Rescindir
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {activeTab === "details" && selectedContract && (
        <section>
          <h2 className="section-title">Detalhes do Contrato {selectedContract.id}</h2>
          <div className="panel">
            <div style={{ marginBottom: "1rem" }}>
              <strong>Status:</strong> {selectedContract.status}
              {selectedContract.commercialPoint && (
                <span className="tag" style={{ marginLeft: "1rem" }}>Ponto Comercial</span>
              )}
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <strong>Aluguel:</strong> R$ {selectedContract.rentAmount?.toLocaleString("pt-BR")}
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <strong>Período:</strong> {selectedContract.startDate} até {selectedContract.endDate}
            </div>

            <div style={{ marginTop: "2rem" }}>
              <h3>Documentos</h3>
              <button className="btn-secondary" onClick={() => createDocument(selectedContract.id)} style={{ marginBottom: "1rem" }}>
                + Adicionar Documento
              </button>
              {documents.length > 0 ? (
                <div className="grid grid-3">
                  {documents.map((doc) => (
                    <div key={doc.id} className="listing-card">
                      <div className="tag">{doc.type}</div>
                      <h4>{doc.name}</h4>
                      {doc.number && <div style={{ color: "var(--text-secondary)" }}>Nº {doc.number}</div>}
                      {doc.expirationDate && (
                        <div style={{ 
                          color: new Date(doc.expirationDate) < new Date() ? "#dc2626" : "var(--text-secondary)",
                          fontWeight: new Date(doc.expirationDate) < new Date() ? 600 : 400
                        }}>
                          Validade: {doc.expirationDate}
                          {new Date(doc.expirationDate) < new Date() && " ⚠️ Vencido"}
                        </div>
                      )}
                      {doc.issuingAuthority && (
                        <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
                          Emitido por: {doc.issuingAuthority}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: "var(--text-secondary)" }}>Nenhum documento cadastrado.</p>
              )}
            </div>

            <div style={{ marginTop: "2rem" }}>
              <h3>Seguros</h3>
              <button className="btn-secondary" onClick={() => createInsurance(selectedContract.id)} style={{ marginBottom: "1rem" }}>
                + Adicionar Seguro
              </button>
              {insurances.length > 0 ? (
                <div className="grid grid-3">
                  {insurances.map((ins) => (
                    <div key={ins.id} className="listing-card">
                      <div className="tag">{ins.type}</div>
                      <h4>{ins.insuranceCompany}</h4>
                      {ins.policyNumber && <div style={{ color: "var(--text-secondary)" }}>Apólice: {ins.policyNumber}</div>}
                      <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--brand-blue)", margin: "0.75rem 0" }}>
                        Prêmio: R$ {ins.premium?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                      </div>
                      {ins.coverageAmount > 0 && (
                        <div style={{ color: "var(--text-secondary)" }}>
                          Cobertura: R$ {ins.coverageAmount?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                        </div>
                      )}
                      {ins.endDate && (
                        <div style={{ color: "var(--text-secondary)" }}>
                          Válido até: {ins.endDate}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: "var(--text-secondary)" }}>Nenhum seguro cadastrado.</p>
              )}
            </div>

            <div style={{ marginTop: "2rem" }}>
              <h3>Garantias (Fiador/Caução)</h3>
              <button className="btn-secondary" onClick={() => createGuarantee(selectedContract.id)} style={{ marginBottom: "1rem" }}>
                + Adicionar Garantia
              </button>
              {guarantees.length > 0 ? (
                <div className="grid grid-3">
                  {guarantees.map((gua) => (
                    <div key={gua.id} className="listing-card">
                      <div className="tag">{gua.type}</div>
                      <h4>{gua.guarantorName}</h4>
                      {gua.guarantorDocument && (
                        <div style={{ color: "var(--text-secondary)" }}>CPF/CNPJ: {gua.guarantorDocument}</div>
                      )}
                      {gua.amount > 0 && (
                        <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--brand-green)", margin: "0.75rem 0" }}>
                          R$ {gua.amount?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                        </div>
                      )}
                      <div style={{ 
                        padding: "0.25rem 0.75rem",
                        borderRadius: "999px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        display: "inline-block",
                        marginTop: "0.5rem",
                        background: gua.status === "ATIVO" ? "var(--brand-green)20" : "#dc262620",
                        color: gua.status === "ATIVO" ? "var(--brand-green)" : "#dc2626"
                      }}>
                        {gua.status}
                      </div>
                      {gua.guarantorPhone && (
                        <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem", marginTop: "0.5rem" }}>
                          📞 {gua.guarantorPhone}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: "var(--text-secondary)" }}>Nenhuma garantia cadastrada.</p>
              )}
            </div>

            <div style={{ marginTop: "2rem" }}>
              <h3>Alertas</h3>
              {selectedContract.alerts?.map((alert, idx) => (
                <div key={idx} className="tag" style={{ margin: "0.5rem 0" }}>
                  {alert.message} - {alert.dueDate}
                </div>
              ))}
            </div>

            <div style={{ marginTop: "2rem" }}>
              <h3>Cronograma de Cobranças</h3>
              <button className="btn-secondary" onClick={() => generateCharges(selectedContract.id)}>
                Gerar Cobranças
              </button>
              {selectedContract.schedule?.map((charge, idx) => (
                <div key={idx} style={{ margin: "0.5rem 0", padding: "0.5rem", background: "#f5f5f5" }}>
                  {charge.dueDate} - R$ {charge.amount?.toLocaleString("pt-BR")} - {charge.overdue ? "Atrasado" : "Em dia"}
                </div>
              ))}
            </div>

            <div style={{ marginTop: "2rem" }}>
              <h3>Histórico de Versões</h3>
              {selectedContract.versions?.map((version, idx) => (
                <div key={idx} style={{ margin: "0.5rem 0", padding: "0.5rem", background: "#f5f5f5" }}>
                  v{version.versionNumber} - {version.changes} ({version.changedBy}, {version.changedAt})
                </div>
              ))}
            </div>

            <div style={{ marginTop: "2rem" }}>
              <h3>Ações</h3>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button className="btn-secondary" onClick={() => adjustRent(selectedContract.id)}>
                  Reajustar Aluguel
                </button>
                <button className="btn-secondary" onClick={() => createInspection(selectedContract.id, "ENTRADA")}>
                  Vistoria de Entrada
                </button>
                <button className="btn-secondary" onClick={() => createInspection(selectedContract.id, "SAIDA")}>
                  Vistoria de Saída
                </button>
                <button className="btn-secondary" onClick={() => createMaintenance(selectedContract.id)}>
                  Ordem de Serviço
                </button>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
